import React from 'react'
import { Link, usePage, router } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { useClipboard } from '@/hooks/useClipboard'
import type { Credential, VirtualBucket, Account } from '@/types'

interface Props {
  credential: Credential
  virtualBuckets: VirtualBucket[]
  secretAccessKey?: string
}

export default function CredentialsShow({ credential, virtualBuckets, secretAccessKey }: Props) {
  const { currentAccount, isAccountAdmin } = usePage<{
    currentAccount: Account
    isAccountAdmin: boolean
  }>().props

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const { copied: copiedAccessKey, copy: copyAccessKey } = useClipboard()
  const { copied: copiedSecretKey, copy: copySecretKey } = useClipboard()

  const handleDelete = () => {
    router.delete(`/accounts/${currentAccount.slug}/credentials/${credential.id}`)
  }

  const permissionLabels: Record<string, string> = {
    read_write: 'Read & Write',
    read_only: 'Read Only',
    write_only: 'Write Only',
  }

  return (
    <ManagementLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {credential.name}
          </Typography>
          {credential.description && (
            <Typography variant="body2" color="text.secondary">
              {credential.description}
            </Typography>
          )}
        </Box>
        {isAccountAdmin && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={Link}
              href={`/accounts/${currentAccount.slug}/credentials/${credential.id}/edit`}
              variant="outlined"
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          </Box>
        )}
      </Box>

      {secretAccessKey && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            Important: Copy your secret access key now!
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This is the only time it will be shown. Store it securely.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
            <Box component="code" sx={{ flex: 1, fontSize: '0.875rem', fontFamily: 'monospace' }}>
              {secretAccessKey}
            </Box>
            <Tooltip title={copiedSecretKey ? 'Copied!' : 'Copy secret key'}>
              <IconButton size="small" onClick={() => copySecretKey(secretAccessKey)}>
                {copiedSecretKey ? <CheckIcon color="success" /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Credential Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Access Key ID</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Box component="code" sx={{ bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 0.5, fontSize: '0.875rem' }}>
                    {credential.access_key_id}
                  </Box>
                  <Tooltip title={copiedAccessKey ? 'Copied!' : 'Copy access key'}>
                    <IconButton size="small" onClick={() => copyAccessKey(credential.access_key_id)}>
                      {copiedAccessKey ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Permissions</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={permissionLabels[credential.permissions] || credential.permissions}
                    size="small"
                    color={credential.permissions === 'read_write' ? 'primary' : credential.permissions === 'read_only' ? 'success' : 'warning'}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Assigned Virtual Buckets
            </Typography>
            {virtualBuckets.length > 0 ? (
              <List disablePadding>
                {virtualBuckets.map((vb, index) => (
                  <ListItem key={vb.id} divider={index < virtualBuckets.length - 1} disableGutters>
                    <ListItemText
                      primary={vb.name}
                      secondary={vb.base_path ? `Path: ${vb.base_path}` : 'Root access'}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No virtual buckets assigned to this credential.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Credential"
        message={`Are you sure you want to delete "${credential.name}"? This action cannot be undone and any applications using this credential will lose access.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </ManagementLayout>
  )
}
